import { motion, useReducedMotion } from 'framer-motion';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { OnboardingBrand } from './useGettingStartedOnboarding';
import { useUser } from '@/Domain/hooks/User/useUser.hook';
import { cn } from '@/Domain/utils/cn';

const HERO_INITIAL = { opacity: 0, filter: 'blur(8px)', y: 8 } as const;
const HERO_ANIMATE = { opacity: 1, filter: 'blur(0px)', y: 0 } as const;
const HERO_TRANSITION = { duration: 0.6, ease: [0.22, 1, 0.36, 1] } as const;
const BRAND_NAME = 'Quickchat AI';

export const BRAND_GRADIENT = 'linear-gradient(90deg, #d4b830 10%, #4d3cfe 60%)';
export const DISCORD_GRADIENT = 'linear-gradient(90deg, #5865f2 0%, #7d8bff 100%)';

export function renderWelcomeTitle(
  title: string,
  highlight: string,
  gradient: string = BRAND_GRADIENT,
): ReactNode {
  const index = highlight ? title.indexOf(highlight) : -1;
  if (index < 0) return title;
  return (
    <>
      {title.slice(0, index)}
      <span className="bg-clip-text text-transparent" style={{ backgroundImage: gradient }}>
        {highlight}
      </span>
      {title.slice(index + highlight.length)}
    </>
  );
}

export function greetingFirstName(firstName: string | null | undefined): string | null {
  const trimmed = firstName?.trim();
  return trimmed || null;
}

export const OnboardingHero = ({
  brand,
  subtitle,
}: {
  brand: OnboardingBrand;
  subtitle: string;
}) => {
  const { t } = useTranslation('getting_started');
  const { user } = useUser();
  const reduceMotion = useReducedMotion();
  const isDiscord = brand === 'discord';
  const greetingName = greetingFirstName(user?.first_name);
  const welcomeTitle = greetingName
    ? t(
        isDiscord
          ? 'automatic_flow.discord.welcome_title_with_name'
          : 'automatic_flow.welcome_title_with_name',
        { name: greetingName },
      )
    : t(isDiscord ? 'automatic_flow.discord.welcome_title' : 'automatic_flow.welcome_title');
  const welcomeHighlight = greetingName || (isDiscord ? 'Discord' : BRAND_NAME);

  return (
    <div className="mb-4 flex flex-col gap-3 text-center">
      <motion.h1
        className={cn(
          'font-safiro text-[2.75rem] font-medium text-balance leading-[1.1]',
          isDiscord ? 'text-[var(--qc-discord-on-surface)]' : 'text-foreground',
        )}
        data-testid="gs-automatic-welcome"
        initial={reduceMotion ? undefined : HERO_INITIAL}
        animate={HERO_ANIMATE}
        transition={HERO_TRANSITION}
      >
        {renderWelcomeTitle(
          welcomeTitle,
          welcomeHighlight,
          isDiscord ? DISCORD_GRADIENT : BRAND_GRADIENT,
        )}
      </motion.h1>
      <motion.h2
        className={cn(
          'text-lg font-medium',
          isDiscord ? 'text-[var(--qc-discord-muted)]' : 'text-muted-foreground',
        )}
        initial={reduceMotion ? undefined : HERO_INITIAL}
        animate={HERO_ANIMATE}
        transition={{ ...HERO_TRANSITION, delay: 0.4 }}
      >
        {subtitle}
      </motion.h2>
    </div>
  );
};
